/**
 * V2 Log — data access and photo upload. Split out of log.js on 2026-07-25
 * (960 lines against the plan's ~400 budget), unchanged. These were all
 * module-private and stay that way: log.js imports them back, and nothing
 * outside the log flow has ever used them.
 */
import * as gateway from '../../core/gateway.js';
import { cached } from '../../core/query.js';
import { compressImage } from '../../../assets/js/utils.js';

export async function fetchLocation(id) {
  return cached(`location:${id}`, async () => {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.LOCATIONS, {
      select: 'id,display_name,formatted_address',
      filters: { id: `eq.${id}` },
      limit: 1,
    });
    if (error) throw error;
    return data?.[0] || null;
  });
}

export async function searchLocations(term) {
  // Escape PostgREST ilike wildcards in user input
  const safe = term.replace(/[%_*]/g, ' ').trim();
  if (!safe) return [];
  const { data, error } = await gateway.rawQuery(gateway.TABLES.LOCATIONS, {
    select: 'id,display_name,formatted_address',
    filters: { display_name: `ilike.*${safe}*` },
    order: 'display_name.asc',
    limit: 12,
  });
  if (error) throw error;
  const seen = new Set();
  return (data || []).filter((p) => {
    const key = `${p.display_name}|${p.formatted_address || ''}`.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 8);
}

/**
 * Resolve the ordered photo list to storage paths, uploading the new
 * files ({file} items) and passing existing paths ({path} items) through.
 */
export async function resolvePhotos(userId, items, onProgress) {
  const paths = [];
  let uploaded = 0;
  const newCount = items.filter((i) => i.file).length;
  for (const item of items) {
    if (item.path) {
      paths.push(item.path);
      continue;
    }
    uploaded += 1;
    onProgress(`Uploading photo ${uploaded} of ${newCount}…`);
    const compressed = await compressImage(item.file, 1200, 0.8);
    const ext = (item.file.name.split('.').pop() || 'jpg').toLowerCase();
    const filename = `${userId}/${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${ext}`;
    const { error } = await gateway.uploadFile('yoinks', filename, compressed);
    if (error) throw error;
    paths.push(filename);
  }
  return paths;
}

export async function fetchYoinkForEdit(id) {
  const { data, error } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
    select: '*,locations(id,display_name,formatted_address)',
    filters: { id: `eq.${id}` },
    limit: 1,
  });
  if (error) throw error;
  return data?.[0] || null;
}
